import {Dispatch, SetStateAction, useEffect} from 'react';

import {isProcedureRunDesignFlowchartColumnNode} from './ProcedureRunDesignFlowchartColumnNode';
import {isProcedureRunDesignFlowchartNode} from './ProcedureRunDesignFlowchartNode';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

export interface MethodHighlightState {
  hoveredMethodId: string | null;
  selectedMethodId: string | null;
}

export interface UseProcedureRunDesignFlowchartHighlightingArgs {
  methodHighlightState: MethodHighlightState;
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
}

export function applyMethodHighlightState(
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  {hoveredMethodId, selectedMethodId}: MethodHighlightState
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  return nodes.map((node) => {
    if (isProcedureRunDesignFlowchartColumnNode(node)) {
      const {data} = node;
      const isHighlighted = hoveredMethodId === data.methodId || selectedMethodId === data.methodId;
      if (data.isHighlighted === isHighlighted) {
        return node;
      }
      return {...node, data: {...data, isHighlighted}};
    }
    if (isProcedureRunDesignFlowchartNode(node)) {
      const {data} = node;
      const isHovered = hoveredMethodId === data.methodId;
      const isHighlighted = isHovered || selectedMethodId === data.methodId;
      if (data.isHighlighted === isHighlighted && data.isHovered === isHovered) {
        return node;
      }
      return {...node, data: {...data, isHovered, isHighlighted}};
    }
    return node;
  });
}

export function useProcedureRunDesignFlowchartHighlighting({
  methodHighlightState,
  initialColumnNodes,
  initialRows,
  setNodes,
}: UseProcedureRunDesignFlowchartHighlightingArgs): void {
  const {hoveredMethodId, selectedMethodId} = methodHighlightState;
  // Re-apply highlighting after node sync refreshes data from changed conditions or methods.
  useEffect(() => {
    setNodes((currentNodes) => applyMethodHighlightState(currentNodes, {hoveredMethodId, selectedMethodId}));
  }, [hoveredMethodId, selectedMethodId, setNodes, initialColumnNodes, initialRows]);
}
